import type {
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedParagraphNode,
} from 'lexical'
import { $applyNodeReplacement, ParagraphNode } from 'lexical'

export type SerializedStyledParagraphNode = {
  color: string | null
  fontSize: string | null
} & SerializedParagraphNode

export class StyledParagraphNode extends ParagraphNode {
  __color: string | null
  __fontSize: string | null

  constructor(color: string | null, fontSize: string | null, key?: NodeKey) {
    super(key)
    this.__color = color
    this.__fontSize = fontSize
  }

  static getType(): string {
    return 'styled-paragraph'
  }

  static clone(node: StyledParagraphNode): StyledParagraphNode {
    return new StyledParagraphNode(node.__color, node.__fontSize, node.__key)
  }

  // 2. JSON serialization
  static importJSON(serializedNode: SerializedStyledParagraphNode): StyledParagraphNode {
    const node = new StyledParagraphNode(serializedNode.color, serializedNode.fontSize)
    return node
  }

  exportJSON(): SerializedStyledParagraphNode {
    return {
      ...super.exportJSON(),
      type: 'styled-paragraph',
      color: this.__color,
      fontSize: this.__fontSize,
      version: 1,
    }
  }

  // TODO: confirm if it is correct
  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor)

    if (element instanceof HTMLElement) {
      if (this.__color) {
        element.style.color = this.__color
      }
      if (this.__fontSize) {
        element.style.fontSize = this.__fontSize
      }
    }

    return { element }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config)

    if (this.__color) {
      element.style.color = this.__color
    }
    if (this.__fontSize) {
      element.style.fontSize = this.__fontSize
    }

    return element
  }

  // TODO: confirm if it is necessary
  updateDOM(prevNode: StyledParagraphNode, dom: HTMLElement): boolean {
    let updated = false
    if (prevNode.__color !== this.__color) {
      dom.style.color = this.__color || ''
      updated = true
    }
    if (prevNode.__fontSize !== this.__fontSize) {
      dom.style.fontSize = this.__fontSize || ''
      updated = true
    }
    return updated
  }
}

export function $createStyledParagraphNode(
  color: string | null,
  fontSize: string | null
): StyledParagraphNode {
  return $applyNodeReplacement(new StyledParagraphNode(color, fontSize))
}

export function $isStyledParagraphNode(
  node: LexicalNode | null | undefined
): node is StyledParagraphNode {
  return node instanceof StyledParagraphNode
}
